/**
 * Auth redirect utilities - centralized logic for login redirect URLs
 * based on hostname (admin subdomain, tenant subdomain, main domain).
 */

const RESERVED_SUBDOMAINS = ['www', 'api', 'app', 'admin'];
const MAIN_DOMAIN = process.env.REACT_APP_MAIN_DOMAIN || 'portalrapp.com';

/**
 * Check if current hostname is the admin subdomain.
 * @returns {boolean}
 */
export const isAdminSubdomain = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  return hostname === 'admin' || hostname.startsWith('admin.');
};

/**
 * Check if current hostname is a tenant subdomain (e.g., acme.portalrapp.com).
 * @returns {boolean}
 */
export const isTenantSubdomain = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return false;
  }
  if (hostname === MAIN_DOMAIN || hostname === `www.${MAIN_DOMAIN}`) {
    return false;
  }
  if (!hostname.endsWith(`.${MAIN_DOMAIN}`)) {
    return false;
  }
  const subdomain = hostname.replace(`.${MAIN_DOMAIN}`, '');
  return subdomain && !RESERVED_SUBDOMAINS.includes(subdomain);
};

/**
 * Get the login redirect URL based on current hostname context.
 * - Admin subdomain (admin.portalrapp.com, admin.localhost): /platform/admin/login
 * - Tenant subdomain or main domain: /login (stay on current host)
 * @returns {string} Full URL for redirect (e.g., https://admin.portalrapp.com/platform/admin/login)
 */
export const getLoginRedirectUrl = () => {
  if (typeof window === 'undefined') {
    return '/login';
  }
  const origin = window.location.origin;
  if (isAdminSubdomain()) {
    return `${origin}/platform/admin/login`;
  }
  return `${origin}/login`;
};
